"use client";

import { Bell, BellOff, BellRing } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { LiveDot } from "@/components/stream/StreamCard";
import { Avatar } from "@/components/ui/Avatar";
import Link from "@/components/ui/Link";
import { useLang, useUi } from "@/components/ui/use-ui";
import { videoHref } from "@/components/video/VideoCard";
import type { VideoSummary } from "@/lib/api/types";
import { camKey, useCamFavorites } from "@/lib/cam-favorites";
import { cn } from "@/lib/cn";
import { useHydrated, useList } from "@/lib/library";
import { disablePush, enablePush, pushState, type PushState, updatePush } from "./push";

// The bell in the header: new videos of the stars and studios the visitor follows since they last looked,
// and their favourite cam models who are live now (/api/notifications), with a dot counting them; and the
// switch for browser notifications (./push.ts), which say the same with the site closed.

interface LiveFavorite {
  source: string;
  username: string;
  name: string;
  avatar: string | null;
  href: string;
}
interface News {
  videos: VideoSummary[];
  live: LiveFavorite[];
}

const SEEN_KEY = "sweet.notifications.seen";
const LIVE_SEEN_KEY = "sweet.notifications.live";
/** How often the bell looks again while a page stays open. */
const EVERY_MS = 5 * 60_000;

const readNumber = (key: string) => {
  try {
    return Number(window.localStorage.getItem(key)) || 0;
  } catch {
    return 0;
  }
};
const readList = (key: string): string[] => {
  try {
    return JSON.parse(window.localStorage.getItem(key) ?? "[]");
  } catch {
    return [];
  }
};
const store = (key: string, value: unknown) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage blocked: the bell forgets at the next page.
  }
};

export function NotificationsBell() {
  const t = useUi();
  const lang = useLang();
  const hydrated = useHydrated();
  const following = useList("following");
  const cams = useCamFavorites();
  const [open, setOpen] = useState(false);
  const [news, setNews] = useState<News | null>(null);
  const [liveSeen, setLiveSeen] = useState<string[]>([]);
  const [push, setPush] = useState<PushState | null>(null);
  const box = useRef<HTMLDivElement>(null);

  const follows = following.map((p) => p.slug).join(",");
  const camList = cams.map(camKey).join(",");

  useEffect(() => {
    if (!hydrated) return;
    const since = readNumber(SEEN_KEY);
    let live = true;
    const load = () =>
      fetch(`/api/notifications?${new URLSearchParams({ follow: follows, cams: camList, since: String(since) })}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((n: News | null) => {
          if (!live || !n) return;
          setNews(n);
          setLiveSeen(readList(LIVE_SEEN_KEY));
        })
        .catch(() => {});
    if (follows || camList) void load();
    const timer = window.setInterval(() => (follows || camList) && void load(), EVERY_MS);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [hydrated, follows, camList]);

  // Browser notifications follow what the visitor follows.
  useEffect(() => {
    if (!hydrated) return;
    void pushState().then((state) => {
      setPush(state);
      if (state === "on") void updatePush({ follows: follows ? follows.split(",") : [], cams: camList ? camList.split(",") : [], lang });
    });
  }, [hydrated, follows, camList, lang]);

  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("pointerdown", away);
    window.addEventListener("keydown", esc);
    return () => {
      window.removeEventListener("pointerdown", away);
      window.removeEventListener("keydown", esc);
    };
  }, [open]);

  const videos = news?.videos ?? [];
  const live = news?.live ?? [];
  const unseenLive = live.filter((l) => !liveSeen.includes(camKey(l)));
  const count = videos.length + unseenLive.length;

  const markRead = () => {
    const now = Date.now();
    store(SEEN_KEY, now);
    store(LIVE_SEEN_KEY, live.map(camKey));
    setLiveSeen(live.map(camKey));
    setNews((n) => (n ? { ...n, videos: [] } : n));
  };

  const togglePush = async () => {
    const interests = { follows: follows ? follows.split(",") : [], cams: camList ? camList.split(",") : [], lang };
    setPush(push === "on" ? await disablePush() : await enablePush(interests));
  };

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={count ? `${t.notifications.title} (${count})` : t.notifications.title}
        title={t.notifications.title}
        className="relative grid size-11 place-items-center rounded-full text-fg transition-colors hover:bg-white/10"
      >
        <Bell size={23} strokeWidth={1.6} />
        {count > 0 && (
          <span className="absolute right-1.5 top-1.5 grid min-w-[18px] place-items-center rounded-full bg-brand-strong px-1 text-[10.5px] font-semibold leading-[18px] text-white ring-2 ring-ink">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>
      {open && (
        // On a phone the bell isn't at the screen's edge, so the panel spans the screen under the header
        // rather than hanging off its left side.
        <div className="glass-strong fixed inset-x-2 top-[4.25rem] z-50 flex max-h-[calc(100dvh-10rem)] flex-col overflow-hidden rounded-2xl shadow-[0_30px_90px_-20px_rgb(0_0_0/0.9)] sm:absolute sm:inset-x-auto sm:right-0 sm:top-12 sm:max-h-[min(80dvh,560px)] sm:w-[380px]">
          <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
            <h2 className="text-[15px] text-fg">{t.notifications.title}</h2>
            {(videos.length > 0 || unseenLive.length > 0) && (
              <button type="button" onClick={markRead} className="text-[12.5px] text-brand-soft hover:underline">
                {t.notifications.markRead}
              </button>
            )}
          </div>
          <div className="no-scrollbar min-h-0 flex-1 overflow-y-auto px-2 py-2">
            {live.length > 0 && (
              <section className="mb-2">
                <h3 className="px-2 pb-1 pt-1 text-[11.5px] uppercase tracking-[0.08em] text-faint">{t.notifications.liveNow}</h3>
                {live.map((m) => (
                  <Link key={camKey(m)} href={m.href} onClick={() => setOpen(false)} className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-white/[0.06]">
                    <Avatar name={m.name} src={m.avatar} size={40} />
                    <span className="min-w-0 flex-1 truncate text-[14px] text-fg">{m.name}</span>
                    <span className="flex items-center gap-1.5 text-[12px] text-brand-soft">
                      <LiveDot className="size-1.5" />
                      {t.notifications.liveNow}
                    </span>
                  </Link>
                ))}
              </section>
            )}
            {videos.length > 0 && (
              <section>
                <h3 className="px-2 pb-1 pt-1 text-[11.5px] uppercase tracking-[0.08em] text-faint">{t.notifications.newVideos}</h3>
                {videos.map((v) => (
                  <Link key={v.id} href={videoHref(v.id)} onClick={() => setOpen(false)} className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-white/[0.06]">
                    {v.thumb ? (
                      // eslint-disable-next-line @next/next/no-img-element -- the tubes' thumbnails
                      <img src={v.thumb} alt="" loading="lazy" className="aspect-video w-24 shrink-0 rounded-md object-cover" />
                    ) : (
                      <span className="aspect-video w-24 shrink-0 rounded-md bg-surface" />
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="line-clamp-2 text-[13.5px] leading-snug text-fg">{v.title}</span>
                      {(v.creator ?? v.brand) && <span className="mt-0.5 block truncate text-[12px] text-muted">{(v.creator ?? v.brand)!.name}</span>}
                    </span>
                  </Link>
                ))}
              </section>
            )}
            {!live.length && !videos.length && <p className="px-3 py-8 text-center text-[13.5px] leading-relaxed text-muted">{t.notifications.empty}</p>}
          </div>
          <div className="space-y-2 border-t border-line px-4 py-3">
            {push === "unsupported" ? (
              <p className="text-[12.5px] text-muted">{t.notifications.pushUnsupported}</p>
            ) : push === "blocked" ? (
              <p className="flex items-center gap-2 text-[12.5px] text-muted">
                <BellOff size={15} />
                {t.notifications.pushBlocked}
              </p>
            ) : (
              push && (
                <>
                  <button
                    type="button"
                    onClick={togglePush}
                    className={cn("flex w-full items-center justify-center gap-2 rounded-full px-4 py-2 text-[13.5px] transition-colors", push === "on" ? "chip text-soft hover:text-fg" : "bg-brand-strong text-white hover:bg-brand-deep")}
                  >
                    {push === "on" ? <BellOff size={15} /> : <BellRing size={15} />}
                    {push === "on" ? t.notifications.pushOff : t.notifications.pushOn}
                  </button>
                  <p className="text-center text-[12px] text-faint">{push === "on" ? t.notifications.pushEnabled : t.notifications.pushHint}</p>
                </>
              )
            )}
            <Link href="/cams/favorites" onClick={() => setOpen(false)} className="block text-center text-[12.5px] text-brand-soft hover:underline">
              {t.notifications.favorites} →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
